//
// # IvaPanel
//
// Panel showing IVA (Implementation vs Abstract) verification results.
// Single-cell A/B/C checks; recursive bottom-up traversal is a follow-up.
// See iva_proposal.md.
//

import React, { useState } from "react";
import { AlertTriangle, CheckCircle, XCircle, Play, Info } from "lucide-react";
import { useWorkspace, useWorkspaceSnapshot } from "../../context";
import { IdentValidator } from "../../client";
import { useIvaResults } from "./useIvaResults";
import { IvaErrorRow } from "./IvaErrorRow";
import "./ivaPanel.css";

// Format identKey for display: {"kind":"hierpath","path":["lib","cell","view"]} -> "lib/cell/view"
function formatIdentKey(identKey: string): string {
  try {
    const parsedJson: unknown = JSON.parse(identKey);
    const identResult = IdentValidator.validator.validate(parsedJson);
    if (identResult.ok && identResult.val.kind === "hierpath") {
      return identResult.val.path.join("/");
    }
  } catch {
    // fall through
  }
  return identKey;
}

export function IvaPanel() {
  const workspace = useWorkspace();
  const snapshot = useWorkspaceSnapshot();
  const { result, loading } = useIvaResults();
  const [running, setRunning] = useState(false);

  const handleRunIva = async () => {
    setRunning(true);
    try {
      await workspace.runIva();
    } finally {
      setRunning(false);
    }
  };

  const totalErrors = result?.errors.length ?? 0;
  const passed = result?.passed ?? false;

  const isLayoutActive =
    snapshot.activeIdent &&
    workspace.getActiveEditor()?.content.contentKind === "layout";

  const isRunning = running || loading;

  const ivaButtonClass =
    "flex-1 px-3 py-1.5 rounded flex items-center justify-center gap-1.5 text-[11px] " +
    "bg-[var(--color-ide-surface-hover)] hover:bg-[var(--color-ide-surface-selected)] " +
    "text-[var(--color-ide-text)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors";

  // Count errors by kind for the summary chips.
  const errorCounts = result?.errors.reduce<Record<string, number>>(
    (acc, err) => {
      acc[err.kind] = (acc[err.kind] || 0) + 1;
      return acc;
    },
    {}
  );

  return (
    <div className="flex flex-col h-full bg-[var(--color-ide-surface)] text-[var(--color-ide-text)]">
      {/* Panel header */}
      <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-widest text-[var(--color-ide-text-muted)] select-none border-b border-[var(--color-ide-border)]">
        <div className="flex items-center gap-1.5">
          <AlertTriangle size={11} />
          IVA Results
          {result?.identKey && (
            <span className="ml-1 font-normal normal-case tracking-normal text-[var(--color-ide-text-dim)]">
              — {formatIdentKey(result.identKey)}
            </span>
          )}
        </div>
      </div>

      {/* Soundness caveat — surfaces the v1 scope honestly per proposal §4 */}
      <div className="px-3 py-2 flex items-start gap-1.5 text-[10px] text-[var(--color-ide-text-dim)] border-b border-[var(--color-ide-border)] bg-[var(--color-ide-surface-hover)]/30">
        <Info size={11} className="mt-0.5 flex-shrink-0" />
        <span>
          Single-cell self-consistency checks (A/B/C). LVS-invisible shorts
          where a route crosses a child blockage but no child pin are still
          deferred to a follow-up.
        </span>
      </div>

      {/* Content area */}
      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
        {isLayoutActive && (
          <div className="p-2 flex gap-2 border-b border-[var(--color-ide-border)]">
            <button
              className={ivaButtonClass}
              onClick={() => {
                void handleRunIva();
              }}
              disabled={isRunning}
              title="Run Implementation-vs-Abstract verification"
            >
              <Play size={12} />
              Run IVA
            </button>
          </div>
        )}

        {isRunning && (
          <div className="p-3 text-[11px] text-[var(--color-ide-text-muted)]">
            Running IVA...
          </div>
        )}

        {!isLayoutActive && (
          <div className="p-3 text-[11px] text-[var(--color-ide-text-dim)]">
            Open a layout to run IVA.
          </div>
        )}

        {isLayoutActive && !isRunning && !result && (
          <div className="p-3 text-[11px] text-[var(--color-ide-text-dim)]">
            No IVA results yet. Click the button above to verify.
          </div>
        )}

        {!isRunning && result && (
          <div className="p-2">
            {/* Status banner */}
            <div
              className={`px-3 py-2 mb-2 rounded flex items-center gap-2 text-[11px] ${
                passed
                  ? "bg-green-500/10 text-green-400"
                  : "bg-red-500/10 text-red-400"
              }`}
            >
              {passed ? <CheckCircle size={14} /> : <XCircle size={14} />}
              {passed
                ? "IVA Clean"
                : `${totalErrors} Error${totalErrors !== 1 ? "s" : ""} Found`}
            </div>

            {/* Error summary chips */}
            {!passed && errorCounts && (
              <div className="px-2 pb-2 text-[10px] text-[var(--color-ide-text-muted)] flex flex-wrap gap-2">
                {errorCounts.instance_outside_outline && (
                  <span className="px-1.5 py-0.5 rounded bg-orange-500/20 text-orange-400">
                    {errorCounts.instance_outside_outline} Outside outline
                  </span>
                )}
                {errorCounts.missing_outline && (
                  <span className="px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-400">
                    {errorCounts.missing_outline} Missing outline
                  </span>
                )}
                {errorCounts.route_outside_blockage && (
                  <span className="px-1.5 py-0.5 rounded bg-red-500/20 text-red-400">
                    {errorCounts.route_outside_blockage} Route uncovered
                  </span>
                )}
                {errorCounts.instance_blockage_outside_parent_blockage && (
                  <span className="px-1.5 py-0.5 rounded bg-red-500/20 text-red-400">
                    {errorCounts.instance_blockage_outside_parent_blockage}{" "}
                    Instance blockage uncovered
                  </span>
                )}
                {errorCounts.pin_not_on_blockage_boundary && (
                  <span className="px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-400">
                    {errorCounts.pin_not_on_blockage_boundary} Pin off boundary
                  </span>
                )}
                {errorCounts.non_convex_blockages_on_layer && (
                  <span className="px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-400">
                    {errorCounts.non_convex_blockages_on_layer} Non-convex
                    blockages
                  </span>
                )}
              </div>
            )}

            {/* Error list */}
            {result.errors.length > 0 && (
              <div className="flex flex-col gap-1">
                {result.errors.map((error, index) => (
                  <IvaErrorRow key={index} error={error} index={index} />
                ))}
              </div>
            )}

            {/* Stats */}
            <div className="mt-3 px-2 text-[10px] text-[var(--color-ide-text-dim)]">
              <div>Instances checked: {result.stats.instanceCount}</div>
              <div>
                Route segments: {result.stats.routeSegmentCount}, instance
                blockages: {result.stats.instanceBlockageCount}
              </div>
              <div>Pins checked: {result.stats.pinCount}</div>
              <div>Check time: {result.stats.checkTimeUs} µs</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
